import {
	useMutation,
	useSuspenseInfiniteQuery,
	useSuspenseQuery,
} from "@tanstack/react-query";
import {
	Calendar,
	Check,
	CreditCard,
	Download,
	ExternalLink,
	FileText,
	Info,
	Loader2,
	MapPin,
	Pencil,
	Plus,
} from "lucide-react";
import { Suspense, useState } from "react";
import { Badge } from "@/components/catalyst/Badge";
import { Button } from "@/components/catalyst/Button";
import { Divider } from "@/components/catalyst/Divider";
import { Heading, Subheading } from "@/components/catalyst/Heading";
import { Text } from "@/components/catalyst/Text";
import { orpc } from "@/orpc/client";
import { getCountryByCode } from "@/util/countries";
import { formatShortDate } from "@/util/date";
import { formatCurrency } from "@/util/format";
import { UpdateBillingAddressDialog } from "./UpdateBillingAddressDialog";
import { UpdatePaymentMethodDialog } from "./UpdatePaymentMethodDialog";

export function BillingSettings() {
	const [showPaymentDialog, setShowPaymentDialog] = useState(false);
	const [showAddressDialog, setShowAddressDialog] = useState(false);

	const openPortal = useMutation(
		orpc.billing.createPortalSession.mutationOptions({
			onSuccess: (data) => {
				window.open(data.url, "_blank");
			},
		}),
	);

	const { data: billingDetails } = useSuspenseQuery(
		orpc.billing.getBillingDetails.queryOptions(),
	);

	const {
		data: invoicesData,
		fetchNextPage,
		hasNextPage,
		isFetchingNextPage,
	} = useSuspenseInfiniteQuery(
		orpc.billing.listInvoices.infiniteOptions({
			input: (pageParam) => ({
				limit: 20,
				startingAfter: pageParam as string | undefined,
			}),
			initialPageParam: undefined as string | undefined,
			getNextPageParam: (lastPage) =>
				lastPage.hasMore ? (lastPage.lastInvoiceId ?? undefined) : undefined,
		}),
	);

	const invoices = invoicesData.pages.flatMap((page) => page.invoices);

	const hasAddress =
		billingDetails?.billingAddress?.city &&
		billingDetails.billingAddress.state &&
		billingDetails.billingAddress.postalCode;

	return (
		<div className="space-y-6">
			<div>
				<Heading>Billing</Heading>
				<Subheading>
					Manage your payment methods, invoices, and billing information.
				</Subheading>
			</div>

			{/* Payment Method & Billing Address - 2 Column Grid */}
			<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
				{/* Payment Method Card */}
				<div className="p-5 rounded-xl border border-base-content/10 bg-base-100 flex flex-col">
					<div className="flex items-start justify-between mb-4">
						<div className="p-3 rounded-lg bg-primary/10">
							<CreditCard className="size-6 text-primary" />
						</div>
						{billingDetails.paymentMethods.length > 0 ? (
							<Badge color="green" className="gap-1">
								<Check className="size-3" />
								Active
							</Badge>
						) : (
							<Badge color="amber">Required</Badge>
						)}
					</div>

					<Text className="font-semibold text-base-content mb-3">
						Payment Method
					</Text>

					{billingDetails.paymentMethods.length > 0 ? (
						<>
							<div className="p-4 rounded-lg bg-base-200/50 border border-base-content/5 mb-3">
								<div className="flex items-center gap-3 mb-1">
									<CreditCard className="size-5 text-base-content/60" />
									<Text className="font-medium text-base-content capitalize">
										{billingDetails.paymentMethods[0].brand} ••••{" "}
										{billingDetails.paymentMethods[0].last4}
									</Text>
								</div>
								<Text className="text-base-content/50 text-sm ml-8">
									Expires{" "}
									{billingDetails.paymentMethods[0].expMonth
										.toString()
										.padStart(2, "0")}
									/{billingDetails.paymentMethods[0].expYear}
								</Text>
							</div>
							<Text className="text-base-content/60 text-sm mb-4">
								Cardholder: {billingDetails.billingName}
							</Text>
						</>
					) : (
						<Text className="text-base-content/50 mb-4 flex-1">
							No payment method on file
						</Text>
					)}

					{billingDetails.paymentMethods.length > 0 ? (
						<Button
							className="w-full mt-auto"
							outline
							onClick={() => setShowPaymentDialog(true)}
						>
							<Pencil className="size-4 mr-2" />
							Update Card
						</Button>
					) : (
						<Button
							className="w-full mt-auto"
							color="blue"
							onClick={() => setShowPaymentDialog(true)}
						>
							<Plus className="size-4 mr-2" />
							Add Card
						</Button>
					)}
				</div>

				{/* Billing Address Card */}
				<div className="p-5 rounded-xl border border-base-content/10 bg-base-100 flex flex-col">
					<div className="flex items-start justify-between mb-4">
						<div className="p-3 rounded-lg bg-violet-500/10">
							<MapPin className="size-6 text-violet-400" />
						</div>
						{billingDetails.billingAddress && (
							<Badge color="green" className="gap-1">
								<Check className="size-3" />
								Saved
							</Badge>
						)}
					</div>

					<Text className="font-semibold text-base-content mb-3">
						Billing Address
					</Text>

					{billingDetails.billingAddress ? (
						<div className="p-4 rounded-lg bg-base-200/50 border border-base-content/5 mb-4 flex-1">
							<div className="text-base-content/80 text-sm space-y-1">
								<Text className="font-medium text-base-content">
									{billingDetails.billingName}
								</Text>
								<Text>{billingDetails.billingAddress.line1}</Text>
								{billingDetails.billingAddress.line2 && (
									<Text>{billingDetails.billingAddress.line2}</Text>
								)}
								<Text>
									{billingDetails.billingAddress.city}
									{hasAddress ? "," : ""} {billingDetails.billingAddress.state}{" "}
									{billingDetails.billingAddress.postalCode}
								</Text>
								<Text>
									{getCountryByCode(billingDetails.billingAddress.country)}
								</Text>
							</div>
						</div>
					) : (
						<Text className="text-base-content/50 mb-4 flex-1">
							No billing address configured
						</Text>
					)}

					<Button
						className="w-full mt-auto"
						outline
						onClick={() => setShowAddressDialog(true)}
					>
						{billingDetails.billingAddress ? (
							<>
								<Pencil className="size-4 mr-2" />
								Update Address
							</>
						) : (
							<>
								<Plus className="size-4 mr-2" />
								Add Address
							</>
						)}
					</Button>
				</div>
			</div>

			{/* Billing Period - Stripe Portal */}
			<div className="p-5 rounded-xl border border-base-content/10 bg-base-100">
				<div className="flex items-center justify-between gap-4">
					<div className="flex items-center gap-4">
						<div className="p-3 rounded-lg bg-primary/10">
							<Calendar className="size-6 text-primary" />
						</div>
						<div>
							<Text className="font-semibold text-base-content">
								Billing Period
							</Text>
							<Text className="text-base-content/60 text-sm">
								Switch between monthly and yearly billing in Stripe
							</Text>
						</div>
					</div>
					<Button
						outline
						onClick={() => openPortal.mutate({})}
						disabled={openPortal.isPending}
					>
						{openPortal.isPending ? (
							"Opening..."
						) : (
							<>
								Manage in Stripe
								<ExternalLink className="size-4 ml-1.5" />
							</>
						)}
					</Button>
				</div>
			</div>

			{/* Security Notice */}
			<div className="flex items-center gap-3 px-4 rounded-lg bg-base-200/50">
				<Info className="size-5 text-base-content/40 shrink-0" />
				<Text className="text-base-content/60 text-sm">
					Your payment information is securely stored in Stripe. We use Stripe
					to process all transactions.
				</Text>
			</div>

			<Divider />

			{/* Invoice History */}
			<div>
				<Heading level={3} className="mb-4">
					Invoice History
				</Heading>

				{invoices.length > 0 ? (
					<div className="rounded-xl border border-base-content/10 bg-base-100 overflow-hidden">
						<div className="overflow-x-auto">
							<table className="w-full text-left text-sm">
								<thead className="bg-base-200/50 border-b border-base-content/10 text-base-content/60">
									<tr>
										<th className="px-4 py-3 font-medium">Invoice</th>
										<th className="px-4 py-3 font-medium">Date</th>
										<th className="px-4 py-3 font-medium">Amount</th>
										<th className="px-4 py-3 font-medium">Status</th>
										<th className="px-4 py-3 text-right font-medium">
											Download
										</th>
									</tr>
								</thead>
								<tbody className="divide-y divide-base-content/10">
									{invoices.map((inv) => (
										<tr key={inv.id} className="group hover:bg-base-200/30">
											<td className="px-4 py-3 font-medium text-base-content">
												{inv.number || "Draft"}
											</td>
											<td className="px-4 py-3 text-base-content/80">
												{formatShortDate(inv.created)}
											</td>
											<td className="px-4 py-3 text-base-content/80">
												{formatCurrency(inv.amountPaid, inv.currency)}
											</td>
											<td className="px-4 py-3">
												<Badge
													color={
														inv.status === "paid"
															? "green"
															: inv.status === "open"
																? "yellow"
																: "zinc"
													}
													className="capitalize"
												>
													{inv.status}
												</Badge>
											</td>
											<td className="px-4 py-3 text-right">
												{inv.pdfUrl && (
													<a
														href={inv.pdfUrl}
														target="_blank"
														rel="noopener noreferrer"
														className="inline-flex items-center text-primary hover:text-primary/80 transition-colors"
													>
														<Download className="size-4 mr-1" />
														PDF
													</a>
												)}
											</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
						{hasNextPage && (
							<div className="p-4 border-t border-base-content/10">
								<Button
									outline
									className="w-full"
									onClick={() => fetchNextPage()}
									disabled={isFetchingNextPage}
								>
									{isFetchingNextPage ? (
										<>
											<Loader2 className="size-4 mr-2 animate-spin" />
											Loading...
										</>
									) : (
										"Load More"
									)}
								</Button>
							</div>
						)}
					</div>
				) : (
					<div className="p-8 rounded-xl border border-dashed border-base-content/20 text-center">
						<FileText className="size-8 text-base-content/40 mx-auto mb-3" />
						<Text className="text-base-content/60">No invoices found</Text>
					</div>
				)}
			</div>

			{/* Dialogs */}
			{showPaymentDialog && (
				<Suspense fallback={null}>
					<UpdatePaymentMethodDialog
						isOpen={showPaymentDialog}
						onClose={() => setShowPaymentDialog(false)}
					/>
				</Suspense>
			)}

			{showAddressDialog && (
				<UpdateBillingAddressDialog
					isOpen={showAddressDialog}
					onClose={() => setShowAddressDialog(false)}
					currentAddress={billingDetails.billingAddress}
					currentName={billingDetails.billingName}
				/>
			)}
		</div>
	);
}
